import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { Editor } from 'primeng/editor';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import {
  AddressBookItemWithLetter,
  AttachmentTypeItem,
  EventContact,
  StatusWorkflowReplaceConfigurationItem,
  WorkflowTransitionEmailConfig,
  WorkflowTransitionEmailRecipient,
} from 'src/app/api/models';
import { AddressBookService, CommonService } from 'src/app/api/services';
import { fieldType } from 'src/app/shared/pipes/field-type.pipe';
import { DYNAMIC_RECIPIENT_OPTIONS, DynamicRecipientKind } from 'src/app/shared/dynamic-recipients';
import { environment } from 'src/environments/environment';

export interface EmailStatusConfigDialogData {
  /** 'ticket' or 'delivery' */
  entityType: 'ticket' | 'delivery';
  statusId: number;
  statusDescription: string;
  /** When set, manages the agreement override instead of the global status config */
  agreementId?: number;
}

type ConfigWithContacts = Omit<WorkflowTransitionEmailConfig, 'to'> & {
  to: {
    to: EventContact[];
    cc: EventContact[];
    bcc: EventContact[];
    to_dynamic: DynamicRecipientKind[];
    cc_dynamic: DynamicRecipientKind[];
    bcc_dynamic: DynamicRecipientKind[];
  }[];
};

const emptyRecipients = () => ({
  to: [],
  cc: [],
  bcc: [],
  to_dynamic: [],
  cc_dynamic: [],
  bcc_dynamic: [],
});

@Component({
  selector: 'app-modal-email-status-config',
  templateUrl: './modal-email-status-config.component.html',
  styleUrls: ['./modal-email-status-config.component.scss'],
})
export class ModalEmailStatusConfigComponent implements OnInit {
  data: EmailStatusConfigDialogData;

  isLoading = false;
  isSaving = false;
  isDeleting = false;
  hasConfig = false;

  selectedConfiguration: ConfigWithContacts = {
    attachments: [],
    body: '',
    subject: '',
    to: [emptyRecipients()],
  };

  suggested_addressbook_contacts: AddressBookItemWithLetter[] = [];
  contacts: Map<number, EventContact> = new Map();
  attachmentsByType: AttachmentTypeItem[] = [];
  replaceFields: MenuItem[] = [];

  @ViewChild('editor') editor?: Editor;
  @ViewChild('subjectInput') subjectInput?: ElementRef<HTMLInputElement>;
  @ViewChild('formRef') formRef?: NgForm;
  lastFocusedField: 'subject' | 'body' = 'body';

  constructor(
    private readonly http: HttpClient,
    private readonly dialogConfig: DynamicDialogConfig,
    public readonly dialogRef: DynamicDialogRef,
    private readonly addressBookService: AddressBookService,
    private readonly commonService: CommonService,
    private readonly messageService: MessageService
  ) {
    this.data = this.dialogConfig.data as EmailStatusConfigDialogData;
  }

  get dynamicRecipientOptions() {
    return DYNAMIC_RECIPIENT_OPTIONS[this.data.entityType];
  }

  async ngOnInit() {
    const entityTypeId = this.data.entityType === 'ticket' ? 2 : 4;
    await Promise.all([
      this.loadReplaceFields(entityTypeId),
      this.loadAttachmentsType(entityTypeId),
    ]);
    await this.loadConfiguration();
  }

  private get apiBase() {
    return environment.baseUrl;
  }

  private async loadConfiguration() {
    this.isLoading = true;
    try {
      let url: string;
      if (this.data.agreementId) {
        const segment =
          this.data.entityType === 'ticket'
            ? 'ticket-status-email-configs'
            : 'delivery-status-email-configs';
        url = `${this.apiBase}/agreements/${this.data.agreementId}/${segment}/${this.data.statusId}`;
      } else {
        const segment = this.data.entityType === 'ticket' ? 'ticket-status' : 'delivery-status';
        url = `${this.apiBase}/${segment}/${this.data.statusId}/email-configuration`;
      }

      const response: any = await firstValueFrom(this.http.get(url));
      const raw: WorkflowTransitionEmailConfig | null = this.data.agreementId
        ? (response?.data?.email_configuration ?? null)
        : (response?.data?.email_configuration ?? null);

      if (raw) {
        this.hasConfig = true;
        await this.fillConfiguration(raw);
      }
    } catch (e: any) {
      if (e?.status !== 404) {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare la configurazione.',
        });
      }
    } finally {
      this.isLoading = false;
    }
  }

  private async fillConfiguration(config: WorkflowTransitionEmailConfig) {
    const contact_ids = config.to.flatMap(el => [
      ...(el.to ?? []),
      ...(el.cc ?? []),
      ...(el.bcc ?? []),
    ]);
    const dynamic = (t: WorkflowTransitionEmailRecipient) => ({
      to_dynamic: t.to_dynamic ?? [],
      cc_dynamic: t.cc_dynamic ?? [],
      bcc_dynamic: t.bcc_dynamic ?? [],
    });
    if (contact_ids.length === 0) {
      this.selectedConfiguration = {
        ...config,
        to: config.to.map(t => ({ to: [], cc: [], bcc: [], ...dynamic(t) })),
      };
      return;
    }
    const response = await firstValueFrom(
      this.addressBookService.addressBookControllerGetAll({ contact_ids, limit: 1000, skip: 0 })
    );
    const list = response.data.list;
    this.selectedConfiguration = {
      ...config,
      to: config.to.map(t => ({
        to: (t.to ?? []).map(id => list.find(ab => ab.id === id) as EventContact).filter(Boolean),
        cc: (t.cc ?? []).map(id => list.find(ab => ab.id === id) as EventContact).filter(Boolean),
        bcc: (t.bcc ?? []).map(id => list.find(ab => ab.id === id) as EventContact).filter(Boolean),
        ...dynamic(t),
      })),
    };
  }

  searchAddressBook(e: AutoCompleteCompleteEvent) {
    this.addressBookService
      .addressBookControllerGetAll({ query: e.query, limit: 20, skip: 0 })
      .subscribe(r => {
        this.suggested_addressbook_contacts = r.data.list;
        r.data.list.forEach(c => {
          if (!this.contacts.has(c.id)) this.contacts.set(c.id, c);
        });
      });
  }

  async save(form: NgForm) {
    if (form.invalid) return;
    this.isSaving = true;
    const configuration: WorkflowTransitionEmailConfig = {
      attachments: this.selectedConfiguration.attachments ?? [],
      body: this.selectedConfiguration.body,
      subject: this.selectedConfiguration.subject,
      to: this.selectedConfiguration.to.map(t => ({
        to: t.to.map(c => c.id),
        cc: t.cc.map(c => c.id),
        bcc: t.bcc.map(c => c.id),
        to_dynamic: t.to_dynamic,
        cc_dynamic: t.cc_dynamic,
        bcc_dynamic: t.bcc_dynamic,
      })),
    };

    try {
      let url: string;
      if (this.data.agreementId) {
        const segment =
          this.data.entityType === 'ticket'
            ? 'ticket-status-email-configs'
            : 'delivery-status-email-configs';
        url = `${this.apiBase}/agreements/${this.data.agreementId}/${segment}/${this.data.statusId}`;
      } else {
        const segment = this.data.entityType === 'ticket' ? 'ticket-status' : 'delivery-status';
        url = `${this.apiBase}/${segment}/${this.data.statusId}/email-configuration`;
      }

      await firstValueFrom(this.http.put(url, { configuration }));
      this.hasConfig = true;
      this.messageService.add({
        severity: 'success',
        summary: 'Conferma',
        detail: 'Configurazione salvata.',
      });
      this.dialogRef.close(true);
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: e?.error?.message || 'Errore durante il salvataggio.',
      });
    } finally {
      this.isSaving = false;
    }
  }

  async deleteConfig() {
    this.isDeleting = true;
    try {
      let url: string;
      if (this.data.agreementId) {
        const segment =
          this.data.entityType === 'ticket'
            ? 'ticket-status-email-configs'
            : 'delivery-status-email-configs';
        url = `${this.apiBase}/agreements/${this.data.agreementId}/${segment}/${this.data.statusId}`;
      } else {
        const segment = this.data.entityType === 'ticket' ? 'ticket-status' : 'delivery-status';
        url = `${this.apiBase}/${segment}/${this.data.statusId}/email-configuration`;
      }

      await firstValueFrom(this.http.delete(url));
      this.hasConfig = false;
      this.selectedConfiguration = {
        attachments: [],
        body: '',
        subject: '',
        to: [emptyRecipients()],
      };
      this.messageService.add({
        severity: 'success',
        summary: 'Conferma',
        detail: 'Configurazione rimossa.',
      });
      this.dialogRef.close(false);
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: e?.error?.message || 'Errore durante la rimozione.',
      });
    } finally {
      this.isDeleting = false;
    }
  }

  private loadReplaceFields(entityTypeId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.commonService.commonControllerGetAllKeys({ entityTypeId }).subscribe({
        next: res => {
          this.replaceFields = (res.data.items || []).map(
            item =>
              ({
                label: `{{${item.label}}} (${fieldType(item.field_type)})`,
                command: () => this.addTextToBody(item, this.editor!),
                icon: 'pi pi-plus',
                tooltipOptions: {
                  appendTo: 'body',
                  tooltipPosition: 'left',
                  tooltipLabel: item.description,
                },
              }) as MenuItem
          );
          resolve();
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: err?.error?.message || 'Errore caricamento campi dinamici.',
          });
          reject();
        },
      });
    });
  }

  private loadAttachmentsType(type: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.commonService.commonControllerGetAttachmentTypes({ type }).subscribe({
        next: res => {
          this.attachmentsByType = res.data.list;
          resolve();
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: err?.error?.message || 'Errore caricamento allegati.',
          });
          reject();
        },
      });
    });
  }

  private addTextToBody(rf: StatusWorkflowReplaceConfigurationItem, editor: Editor) {
    const text = `{{${rf.label}}}`;
    if (this.lastFocusedField === 'subject' && this.subjectInput) {
      const input = this.subjectInput.nativeElement;
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? input.value.length;
      this.selectedConfiguration.subject =
        input.value.substring(0, start) + text + input.value.substring(end);
      setTimeout(() => {
        input.setSelectionRange(start + text.length, start + text.length);
        input.focus();
      });
    } else {
      const quill = editor.getQuill();
      const caretPosition = quill.getSelection(true);
      quill.insertText(caretPosition, text, 'user');
      setTimeout(() => this.formRef?.controls['body']?.updateValueAndValidity(), 1000);
    }
  }
}
